import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import type { MentalHealthCareProvider } from "@thalamos/aspire-common";

import {
  Banner,
  BannerList,
  Button,
  PopupDialog,
} from "~/components/design-system/index.js";

export interface DeleteMentalHealthCareProviderModalProps {
  open: boolean;
  onClose: () => void;
  provider: MentalHealthCareProvider;
  onConfirm: () => Promisable<void>;
}

export function DeleteMentalHealthCareProviderModal({
  open,
  onClose,
  provider,
  onConfirm,
}: DeleteMentalHealthCareProviderModalProps) {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  // Only the confirm button is disabled while the delete runs — Cancel stays
  // live so a hung request cannot trap the admin in the dialog. `finally`
  // matters: a rejecting `onConfirm` would otherwise disable it permanently.
  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <PopupDialog open={open} onClose={onClose}>
      <DialogTitle>
        {t("pages.mentalHealthCareProviderManagement.deleteModal.title")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography>
          {t("pages.mentalHealthCareProviderManagement.deleteModal.content")}
        </Typography>
        <Typography sx={{ fontWeight: "bold", margin: "2em 0" }}>
          {provider.name}
          <br />
          {provider.email}
        </Typography>
        <Banner
          bannerType={BannerList.WARNING}
          body={t(
            "pages.mentalHealthCareProviderManagement.deleteModal.warning",
          )}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", mt: 2 }}>
        <Button
          label={t(
            "pages.mentalHealthCareProviderManagement.deleteModal.cancel",
          )}
          onClick={onClose}
          variant="outlined"
        />
        <Button
          label={t(
            "pages.mentalHealthCareProviderManagement.deleteModal.confirm",
          )}
          onClick={() => {
            void confirmDelete();
          }}
          color="error"
          disabled={isDeleting}
        />
      </DialogActions>
    </PopupDialog>
  );
}
